import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, User, Package, Heart, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import { EmailLoginForm } from "@/components/store/EmailLoginForm";

export default function Account() {
  const { user, loading, login, logout, setUser } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "My Account · Dealswagon";
    if (user) setForm({ name: user.name || "", phone: user.phone || "", address: user.address || "" });
  }, [user]);

  if (loading) return <div className="max-w-4xl mx-auto p-10">Loading...</div>;

  if (!user) {
    return (
      <main className="max-w-md mx-auto px-4 py-16 text-center" data-testid="account-signin">
        <User className="h-12 w-12 mx-auto text-slate-300" />
        <h1 className="font-heading text-3xl text-brand-navy mt-4">Sign in to Dealswagon</h1>
        <p className="text-slate-600 mt-1">Access your wishlist, orders and future holds.</p>
        <div className="mt-6"><EmailLoginForm /></div>
        <div className="my-4 text-xs uppercase tracking-widest text-slate-400 font-bold">or</div>
        <button onClick={login} data-testid="signin-google-btn"
          className="inline-flex items-center gap-2 bg-brand-navy text-white rounded-full h-12 px-6 font-bold">
          Continue with Google
        </button>
      </main>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/auth/profile", form);
      setUser(r.data);
      toast.success("Profile saved");
    } catch { toast.error("Could not save"); }
    setSaving(false);
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="account-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div className="flex items-center gap-4">
          {user.picture && <img src={user.picture} alt="" className="h-14 w-14 rounded-full" />}
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl text-brand-navy">Welcome, {user.name?.split(" ")[0] || "there"}</h1>
            <div className="text-sm text-slate-500">{user.email}</div>
            {user.role === "SUPER_ADMIN" && (
              <span className="inline-flex items-center gap-1 mt-1 bg-brand-red text-white text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full">
                <Shield className="h-3 w-3" /> Super Admin
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {user.role === "SUPER_ADMIN" && (
            <Link to="/admin" data-testid="account-admin-link" className="inline-flex items-center gap-1 bg-brand-navy text-white rounded-full px-4 h-10 text-sm font-bold">
              <Shield className="h-4 w-4" /> Admin
            </Link>
          )}
          <button onClick={logout} data-testid="account-logout-btn" className="inline-flex items-center gap-1 border border-slate-200 rounded-full px-4 h-10 text-sm font-bold hover:bg-slate-50">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <Tile icon={Package} title="My Orders" text="Track future orders" to="#" comingSoon />
        <Tile icon={Heart} title="My Wishlist" text="Saved items" to="/wishlist" />
        <Tile icon={Package} title="My Holds" text="24-hour holds" to="#" comingSoon />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-2xl">
        <h2 className="font-heading text-lg text-brand-navy mb-4">Profile</h2>
        <div className="space-y-3">
          <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="profile-name" />
          <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="profile-phone" />
          <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testId="profile-address" />
        </div>
        <button onClick={save} disabled={saving} data-testid="profile-save-btn"
          className="mt-5 bg-brand-navy text-white rounded-full px-6 h-11 font-bold disabled:opacity-60">
          {saving ? "Saving..." : "Save changes"}
        </button>
      </div>
    </main>
  );
}

const Tile = ({ icon: Icon, title, text, to, comingSoon }) => (
  <Link to={to} className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition relative">
    {comingSoon && <span className="absolute top-3 right-3 text-[10px] font-black uppercase tracking-widest bg-brand-yellowLight text-brand-navy px-2 py-0.5 rounded-full">Coming Soon</span>}
    <Icon className="h-6 w-6 text-brand-red" />
    <div className="font-heading text-lg text-brand-navy mt-2">{title}</div>
    <p className="text-sm text-slate-500">{text}</p>
  </Link>
);

const Field = ({ label, value, onChange, testId }) => (
  <label className="block">
    <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{label}</span>
    <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="mt-1 w-full h-11 rounded-lg border border-slate-200 px-3 focus:outline-none focus:ring-2 focus:ring-brand-navy" />
  </label>
);
